import sessionStorage from '@avalara/bat-apps-shared/helpers/sessionStorage';
import { t } from '@avalara/bat-apps-shared/i18n';
import { SCol, SContainer, SRow } from '@avalara/skylab-sdk/react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import conf from '../../../conf';
import { selectState } from '../../../selectors';

// Storage reads can throw the same way the writes in purchaseOffer/createAccountWithOffer can
// (e.g. SecurityError when third-party storage is blocked in an embedded/iframe view) — treat that
// the same as "not confirmed" (redirect to the bare offer page) rather than crashing the page.
const safeGetPurchaseFlag = (key: string): string | null => {
  try {
    return sessionStorage.get(key);
  } catch {
    return null;
  }
};

// Post-purchase page for offers reached through the Product Catalog / PDP flow — rendered by
// OfferContainer at /${offerFlow}/thank-you in place of the classic ThankYouPage for any offer
// whose `pages` include Pages.OrderReview (the same offers Order Review itself serves).
const OrderConfirmation = () => {
  const history = useHistory();
  const {
    account: { accountId },
    offer: { offerFlow, offerName, orderConfirmationContent },
  } = useSelector(selectState);

  // This route is reachable directly (bookmark, back button, typed URL) purely from static offer
  // metadata (pages.includes(Pages.OrderReview)) — nothing about the route itself proves a purchase
  // actually completed. purchaseOffer/createAccountWithOffer set this offer- and account-scoped
  // flag on success; checking both (rather than just offerFlow, or the pre-existing session-wide
  // `hasPurchasedOffer` that stays true for every offer/account once any purchase succeeds)
  // prevents an abandoned checkout, a *different* offer's purchase, and — since sessionStorage
  // survives logout/login and account switching within the same tab — a *different account's*
  // completed purchase from ever showing this offer as falsely confirmed.
  const hasPurchasedOffer =
    !!offerFlow && !!accountId && safeGetPurchaseFlag(`hasPurchasedOffer-${offerFlow}-${accountId}`) === 'true';

  useEffect(() => {
    // Without offerFlow there's no valid offer URL to land on — OfferContainer itself already
    // guarantees offerFlow is populated before any route (including this one) can render, but guard
    // here too rather than ever redirecting to a broken `/undefined`.
    if (!offerFlow || hasPurchasedOffer) return;
    history.replace(`/${offerFlow}`);
  }, [hasPurchasedOffer, offerFlow, history]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [firstSupplementalLine, secondSupplementalLine] = orderConfirmationContent?.supplementalText ?? [];
  const onboardingUrl = orderConfirmationContent?.onboardingUrl;

  if (!hasPurchasedOffer) return null;

  return (
    <SContainer data-ref='buy.order-confirmation.page' className='pad-top-xl'>
      <SRow className='justify-content-center'>
        <SCol span='8' className='flex flex-dir-col align-items-center text-center'>
          <img
            className='pad-bottom-md'
            width={200}
            src={`${conf.ASSET_PATH}/assets/images/avi_main.svg`}
            alt={t('client.accessibility.order-confirmation-avi-image')}
            data-ref='buy.order-confirmation.avi-image'
          />
          <h1 className='pad-bottom-md' data-ref='buy.order-confirmation.heading'>
            {t('client.order-confirmation.heading', { offerName })} 🎉
          </h1>
          {firstSupplementalLine && (
            <p className='pad-bottom-sm' data-ref='buy.order-confirmation.supplemental-text-1'>
              {firstSupplementalLine}
            </p>
          )}
          {secondSupplementalLine && (
            <p className='pad-bottom-lg' data-ref='buy.order-confirmation.supplemental-text-2'>
              {secondSupplementalLine}
            </p>
          )}
          <div className='flex'>
            {onboardingUrl && (
              <a
                className='button primary margin-right-md'
                href={onboardingUrl}
                data-ref='buy.order-confirmation.go-to-onboarding'
              >
                {t('client.order-confirmation.go-to-onboarding')}
              </a>
            )}
            <a
              className={`button ${onboardingUrl ? 'secondary' : 'primary'}`}
              href={conf.HOME_URL}
              data-ref='buy.order-confirmation.go-to-home'
            >
              {t('client.order-confirmation.go-to-home')}
            </a>
          </div>
        </SCol>
      </SRow>
    </SContainer>
  );
};

export default OrderConfirmation;
